import { Bookmark } from '@phosphor-icons/react'
import { Box, Button, Skeleton } from '@mui/material'
import { alpha } from '@mui/material/styles'
import makeStyles from '@mui/styles/makeStyles'
import clsx from 'clsx'
import React from 'react'
import { BookmarkButton, ICON_SIZE } from '../../action-buttons'
import { UserGroupPolicy } from '../../user-group-policy'
import { ALLOW_PLAYLISTS_GROUP } from '../../user-group-policy/permissions'
import { THUMBNAIL_ASPECT_RATIO } from '../../../helpers/content-image'
import PromotedContentCardContent from './card-content'
import PromotedCardContainer from './container'
import usePromotedCardMethods, { PROMOTED_CARD_TYPES } from './use-promoted-card-methods'

// PENG-3630: the "Discover more content" card at the end of an article.
// Media first at 16:9 (the ratio editors upload and crop at, so nothing is
// cut), bookmark on the image's corner, then type/time and title, and "Add to
// Playlist" as a text link. No like or summary, so the card stays short.
// The bookmark chip, and the minimum touch target on phones
const BOOKMARK_SIZE = 36
const TOUCH_TARGET = 44

const useStyles = makeStyles(({ spacing, typography, palette, radius, breakpoints, customShadows }) => ({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  media: {
    position: 'relative',
    margin: spacing(2, 2, 0, 2)
  },
  imageContainer: {
    aspectRatio: THUMBNAIL_ASPECT_RATIO,
    cursor: 'pointer',
    borderRadius: radius.lg,
    '&:focus-visible': {
      outline: `2px solid ${palette.primary.main}`,
      outlineOffset: 2
    }
  },
  image: {
    display: 'block',
    width: '100%',
    height: '100%',
    borderRadius: radius.lg,
    // Inside the article, `article img { object-fit: contain }` wins over this.
    // That is fine: imgix returns photos at exactly 16:9, so both fill the box,
    // and the logo fallback shows whole instead of cropped.
    objectFit: 'cover'
  },
  // Restyles BookmarkButton's icon-only ActionButton as a round chip on the
  // image's corner
  bookmark: {
    '&&': {
      position: 'absolute',
      top: spacing(1),
      right: spacing(1),
      width: BOOKMARK_SIZE,
      height: BOOKMARK_SIZE,
      padding: 0,
      borderRadius: '50%',
      backgroundColor: alpha(palette.white.main, 0.92),
      boxShadow: customShadows.main,
      color: palette.grey.shade[70],
      '&:hover': {
        backgroundColor: palette.white.main
      },
      [breakpoints.down('sm')]: {
        width: TOUCH_TARGET,
        height: TOUCH_TARGET
      }
    }
  },
  bookmarked: {
    '&&': {
      color: palette.primary.shade[60]
    }
  },
  contentContainer: {
    // The body's spacing follows Fibonacci, measured between the text itself
    // (not its line boxes): image 13 type 5 title 13 link 13 bottom. Type and
    // title read as one group; the link sits apart from it.
    paddingTop: 3,
    '&&': {
      // `auto`, not the base calc() or 100%: the content Box carries an 8px
      // margin, and 100% plus that margin pushes the meta row past the edge.
      width: 'auto',
      paddingBottom: 0,
      marginBottom: spacing(1)
    }
  },
  // Plain type label here, not a pill. Line height 1 so the meta row is only
  // as tall as its text and doesn't push the title away.
  cardContentType: {
    '&&': {
      backgroundColor: 'transparent',
      padding: 0,
      '& .MuiTypography-root': {
        lineHeight: 1
      }
    }
  },
  time: {
    '&&': {
      lineHeight: 1
    }
  },
  cardTitle: {
    '&&': {
      ...typography.heading.h6
    }
  },
  cardSummary: {
    '&&': {
      display: 'none'
    }
  },
  actions: {
    '&&': {
      marginTop: 5
    }
  },
  playlistLink: {
    '&&': {
      // Cancels the 8px side padding so the label lines up with the title
      marginLeft: spacing(-1),
      padding: spacing(0.5, 1),
      lineHeight: 1.3,
      textTransform: 'none',
      fontWeight: typography.weight.medium,
      // On phones an invisible layer, centred on the link, stretches its hit
      // area to the touch minimum without moving anything.
      [breakpoints.down('sm')]: {
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          height: TOUCH_TARGET,
          transform: 'translateY(-50%)'
        }
      }
    }
  }
}))

const PromotedContentDiscoverCard = ({ content, onSelect, position, testId, size, onAddToPlaylist, source, bookmarkFrom }) => {
  const classes = useStyles()

  const {
    contentImage,
    handleCardSelect,
    cardSize,
    bookmark: { bookmarked, isBookmarking, onBookmark },
    displayAddToPlaylist,
    handleAddToPlaylist
  } = usePromotedCardMethods({
    content,
    position,
    size,
    cardType: PROMOTED_CARD_TYPES.DISCOVER,
    onSelect,
    onAddToPlaylist,
    source,
    bookmarkFrom
  })

  const handleImageKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleCardSelect()
    }
  }

  const actions = {
    leftActions: displayAddToPlaylist && (
      <UserGroupPolicy
        accessGroup={ALLOW_PLAYLISTS_GROUP}
        componentAllow={
          <Button
            variant='text'
            color='primary'
            size='small'
            onClick={handleAddToPlaylist}
            className={classes.playlistLink}
            data-test={`discover-card-add-to-playlist-${content?.friendlyUrl}`}
          >
            Add to Playlist
          </Button>
        }
      />
    )
  }

  return (
    <PromotedCardContainer size={cardSize} content={content} testId={testId}>
      <Box className={classes.layout}>
        <Box className={classes.media}>
          <Box
            className={classes.imageContainer}
            onClick={handleCardSelect}
            onKeyDown={handleImageKeyDown}
            role='button'
            tabIndex={0}
            aria-label={content ? `View ${content.title} image` : 'Loading content image'}
          >
            {content
              ? (
                <img
                  src={contentImage}
                  className={classes.image}
                  data-test={`card-image-${content?.friendlyUrl}`}
                  alt={content?.image?.altText || ''}
                />
                )
              : <Skeleton variant='rectangular' className={classes.image} />}
          </Box>
          {/* BookmarkButton gates it: signed-out readers get the free-account
              modal, and users outside the bookmarks group get no button */}
          {content && (
            <BookmarkButton
              bookmarked={bookmarked}
              hideText
              loading={isBookmarking}
              onClick={onBookmark}
              startIcon={<Bookmark size={ICON_SIZE} weight={bookmarked ? 'fill' : 'regular'} />}
              className={clsx(classes.bookmark, bookmarked && classes.bookmarked)}
              aria-busy={isBookmarking}
              aria-label={bookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
              data-test={`discover-card-bookmark-${content?.friendlyUrl}`}
            />
          )}
        </Box>
        <PromotedContentCardContent
          content={content}
          actions={actions}
          // Same 8px inset as the vertical Featured cards
          cardType={PROMOTED_CARD_TYPES.SECONDARY}
          onSelect={handleCardSelect}
          // 2px plus the text's own line-height leading leaves 5px between the
          // type and the title; the hidden summary keeps no margin
          defaultSectionMargin={0.25}
          subtitleMargin={0}
          overrides={{
            contentContainer: classes.contentContainer,
            cardContentType: classes.cardContentType,
            time: classes.time,
            cardTitle: classes.cardTitle,
            cardSummary: classes.cardSummary,
            actions: classes.actions
          }}
        />
      </Box>
    </PromotedCardContainer>
  )
}

export default PromotedContentDiscoverCard
